import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import { supabase } from '../config/supabase';
import authService, { UserData } from '../services/authService';

interface AuthContextType {
  user: UserData | null;
  token: string | null;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  loading: boolean;
  error: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<UserData | null>(null);
  const [token, setToken] = useState<string | null>(localStorage.getItem('token'));
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);

  // Écouter les changements d'état d'authentification Supabase
  useEffect(() => {
    const { data: listener } = supabase.auth.onAuthStateChange(async (_event, session) => {
      setLoading(true);

      try {
        if (session?.user) {
          // Utilisateur connecté
          const accessToken = session.access_token;
          localStorage.setItem('token', accessToken);
          setToken(accessToken);

          // Récupérer les données utilisateur complètes depuis la table profiles
          const userData = await authService.getUserById(session.user.id);

          if (userData) {
            setUser(userData);
            setIsAuthenticated(true);
          } else {
            // L'utilisateur existe dans Supabase Auth mais pas de profil correspondant
            console.error('Utilisateur Supabase Auth trouvé mais pas de profil correspondant');
            await authService.logout();
            setUser(null);
            setToken(null);
            setIsAuthenticated(false);
            localStorage.removeItem('token');
          }
        } else {
          // Utilisateur déconnecté
          setUser(null);
          setToken(null);
          setIsAuthenticated(false);
          localStorage.removeItem('token');
        }
      } catch (error) {
        console.error('Erreur lors de la vérification de l\'authentification:', error);
        setUser(null);
        setToken(null);
        setIsAuthenticated(false);
        localStorage.removeItem('token');
      } finally {
        setLoading(false);
      }
    });

    // Nettoyer l'écouteur lors du démontage du composant
    return () => listener.subscription.unsubscribe();
  }, []);

  const login = async (email: string, password: string) => {
    setLoading(true);
    setError(null);

    try {
      console.log('Tentative de connexion pour:', email);
      
      // Utiliser le service d'authentification Supabase
      const authData = await authService.login(email, password);
      
      // Stocker le token dans localStorage
      localStorage.setItem('token', authData.token);
      
      setToken(authData.token);
      
      // Récupérer les données utilisateur complètes
      const userData = await authService.getUserById(authData.user.uid);
      if (userData) {
        setUser(userData);
        setIsAuthenticated(true);
      } else {
        throw new Error('Utilisateur non trouvé dans la base de données');
      }
      
      console.log('Connexion réussie');
    } catch (error: any) {
      console.error('Exception lors de la connexion:', error);
      setError(error.message || 'Erreur de connexion');
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await authService.logout();
      localStorage.removeItem('token');
      setToken(null);
      setUser(null);
      setIsAuthenticated(false);
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
    }
  };

  return (
    <AuthContext.Provider value={{ user, token, isAuthenticated, login, logout, loading, error }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth doit être utilisé à l\'intérieur d\'un AuthProvider');
  }
  return context;
};
